/**
 * Iter51 Etapa 11 — SupplierCombobox
 *
 * Searchable dropdown for energy supplier selection, backed by /api/energy-suppliers.
 * Features:
 *   - Fast async search (debounced 200ms)
 *   - Commodity filter (electric/gas) passed in from parent
 *   - Shows alias hints in dropdown
 *   - Allows free-text fallback (custom value not in catalog) via "Folosește text"
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check, ChevronsUpDown, Building2, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupplierCombobox = ({
  value,
  onChange,
  commodity = null,        // "electric" | "gas" | null (all)
  placeholder = "Alege furnizor...",
  testId = "supplier-combobox",
  className = "",
  allowFreeText = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounced fetch
  useEffect(() => {
    let cancel = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 50 };
        if (commodity && commodity !== "both") params.commodity = commodity;
        if (query.trim()) params.q = query.trim();
        const r = await axios.get(`${API_URL}/energy-suppliers`, { params });
        if (!cancel) setItems(r.data.items || []);
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [query, commodity, open]);

  const selectedDisplay = useMemo(() => value || "", [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal text-sm h-8 ${className}`}
          data-testid={testId}
        >
          <span className="truncate flex items-center gap-1.5 min-w-0">
            {selectedDisplay ? (
              <>
                <Building2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <span className="truncate">{selectedDisplay}</span>
              </>
            ) : (
              <span className="text-slate-400">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Caută furnizor sau alias..."
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId}-input`}
          />
          <CommandList>
            {loading && (
              <div className="px-3 py-4 text-xs text-slate-400 flex items-center gap-2">
                <Search className="h-3 w-3 animate-pulse" /> Caut...
              </div>
            )}
            {!loading && items.length === 0 && (
              <CommandEmpty>
                <div className="px-3 py-4 text-center">
                  <p className="text-xs text-slate-500">Niciun furnizor găsit.</p>
                  {allowFreeText && query.trim() && (
                    <button
                      type="button"
                      className="mt-2 text-xs text-blue-600 hover:underline"
                      onClick={() => { onChange(query.trim()); setOpen(false); }}
                      data-testid={`${testId}-free-text`}
                    >
                      Folosește textul „{query.trim()}"
                    </button>
                  )}
                </div>
              </CommandEmpty>
            )}
            {!loading && items.length > 0 && (
              <CommandGroup heading={`${items.length} furnizor${items.length === 1 ? '' : 'i'}`}>
                {items.map((s) => {
                  const selected = value === s.name;
                  return (
                    <CommandItem
                      key={s.id}
                      value={s.name}
                      onSelect={() => { onChange(s.name); setOpen(false); }}
                      data-testid={`${testId}-option-${s.id}`}
                      className="flex items-start gap-2"
                    >
                      <Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${selected ? "opacity-100 text-blue-600" : "opacity-0"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{s.name}</p>
                        {s.aliases && s.aliases.length > 0 && (
                          <p className="text-[11px] text-slate-400 truncate">
                            alias: {s.aliases.slice(0, 3).join(", ")}
                          </p>
                        )}
                      </div>
                      <span className="text-[10px] text-slate-400 uppercase flex-shrink-0">
                        {s.supplier_type === "electric" ? "⚡" : s.supplier_type === "gas" ? "🔥" : "⚡🔥"}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SupplierCombobox;
